<script setup lang="ts">
const { t } = useI18n()

interface Timeparts {
  hours: string
  minutes: string
  seconds: string
}

const now = ref(new Date())
const isAfternoonPickup = ref(false)
const timeParts = ref<Timeparts>({ hours: '00', minutes: '00', seconds: '00' })

function getCutoffTime(): Date {
  const userDate = new Date()
  const currentHour = userDate.getHours()

  if (currentHour >= 16) {
    // After 4 PM but before midnight: show countdown to morning pickup
    // Morning cutoff at 8 AM (1 hour before 9 AM pickup)
    userDate.setDate(userDate.getDate() + 1)
    userDate.setHours(8, 0, 0, 0)
    isAfternoonPickup.value = false
  }
  else if (currentHour >= 0 && currentHour < 8) {
    // Midnight to 8 AM: show countdown to afternoon pickup at 4 PM
    userDate.setHours(16, 0, 0, 0)
    isAfternoonPickup.value = true
  }
  else {
    // 8 AM to 4 PM: show countdown to 4 PM afternoon pickup
    userDate.setHours(16, 0, 0, 0)
    isAfternoonPickup.value = true
  }

  return userDate
}

const cutoffTime = ref(getCutoffTime())

function updateTimeParts(targetDate: Date, currentDate: Date): void {
  const diff = targetDate.getTime() - currentDate.getTime()

  if (diff <= 0) {
    timeParts.value = { hours: '00', minutes: '00', seconds: '00' }
    return
  }

  const h = Math.floor(diff / (1000 * 60 * 60))
  const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
  const s = Math.floor((diff % (1000 * 60)) / 1000)

  timeParts.value = {
    hours: h.toString().padStart(2, '0'),
    minutes: m.toString().padStart(2, '0'),
    seconds: s.toString().padStart(2, '0'),
  }
}

const formattedCountdownTime = computed(() => {
  return `${timeParts.value.hours}:${timeParts.value.minutes}:${timeParts.value.seconds}`
})

const countdownMessageParts = computed(() => {
  const currentHour = now.value.getHours()

  if (currentHour >= 16) {
    return t('product.order_countdown.morning_message', [formattedCountdownTime.value]).split(formattedCountdownTime.value)
  }
  else {
    return t('product.order_countdown.message', [formattedCountdownTime.value]).split(formattedCountdownTime.value)
  }
})

let timer: NodeJS.Timeout | null = null

onMounted(() => {
  const updateCountdown = () => {
    now.value = new Date()

    const newCutoff = getCutoffTime()
    if (newCutoff.getTime() !== cutoffTime.value.getTime()) {
      cutoffTime.value = newCutoff
    }

    updateTimeParts(cutoffTime.value, now.value)
  }

  updateCountdown()
  timer = setInterval(updateCountdown, 1000)
})

onBeforeUnmount(() => {
  if (timer) {
    clearInterval(timer)
  }
})
</script>

<template>
  <div class="mx-auto mb2 w-full flex items-center">
    <div class="countdown-wrapper w-full flex items-center rd-md bg-warning/60 py1 text-center">
      <div class="w-full flex items-center justify-center px-2">
        <p class="text-xs text-black font-semibold">
          <LazyUiTooltipProvider>
            <LazyUiTooltip>
              <LazyUiTooltipTrigger>
                <span class="flex flex-wrap cursor-pointer items-center justify-center gap-0.5 text-center">
                  {{ countdownMessageParts[0] }}
                  <span class="mx-1 flex items-center gap-[1px]">
                    <span class="relative inline-block rounded bg-black px-1 py-0.5 text-white font-bold tabular-nums">
                      {{ timeParts.hours }}
                    </span>
                    <span class="mx-0.5">:</span>
                    <span class="relative inline-block rounded bg-black px-1 py-0.5 text-white font-bold tabular-nums">
                      {{ timeParts.minutes }}
                    </span>
                    <span class="mx-0.5">:</span>
                    <span class="relative inline-block rounded bg-black px-1 py-0.5 text-white font-bold tabular-nums">
                      {{ timeParts.seconds }}
                    </span>
                  </span>
                  {{ countdownMessageParts[1] }}
                </span>
              </LazyUiTooltipTrigger>
              <LazyUiTooltipContent>
                <div class="max-w-xs">
                  {{ t('product.order_countdown.tooltip') }}
                </div>
              </LazyUiTooltipContent>
            </LazyUiTooltip>
          </LazyUiTooltipProvider>
        </p>
      </div>
    </div>
  </div>
</template>
